import React, { useEffect, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import { Plus, Trash2, Copy, CalendarDays, RotateCcw, ListChecks } from "lucide-react";
import { toast } from "sonner";
import { useWizard } from "@/context/WizardContext";
import { Grid, TextInput, SectionHeading } from "@/components/wizard/FormKit";
import NarrativeField from "@/components/wizard/NarrativeField";
import { buildDefaultStaffingSchedule } from "@/lib/pcwCatalog";

const DAYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const WEEKDAYS = ["mon","tue","wed","thu","fri"];
const WEEKEND  = ["sat","sun"];

const ACTIVITY_ROW_TYPES = [
  { key: "school", label: "School" },
  { key: "work", label: "Work" },
  { key: "day_program", label: "Day Program" },
  { key: "other1", label: "Other 1" },
  { key: "other2", label: "Other 2" },
];

const STAFFING_ROW_TYPES = [
  { key: "skilled_nursing", label: "Skilled Nursing Services" },
  { key: "hha", label: "Home Health Aide Services" },
  { key: "pcw", label: "Personal Care Worker Services" },
  { key: "case_sharing", label: "Case Sharing (Specify agencies)" },
  { key: "other", label: "Other (Specify)" },
];

function KeyedGrid({ rowTypes, grid, onChange, testidPrefix }) {
  const setCell = (rowKey, day, value) => {
    onChange({ ...grid, [rowKey]: { ...(grid[rowKey] || {}), [day]: value } });
  };
  const setSpecify = (rowKey, value) => {
    onChange({ ...grid, [rowKey]: { ...(grid[rowKey] || {}), specify: value } });
  };
  const copyMondayAll = (rowKey) => {
    const row = grid[rowKey] || {};
    const v = row.mon || "";
    const next = { ...(grid[rowKey] || {}) };
    DAYS.forEach((d) => { next[d] = v; });
    onChange({ ...grid, [rowKey]: next });
    toast.success("Copied Monday to all 7 days");
  };
  const copyWeekdaysToWeekend = (rowKey) => {
    const row = grid[rowKey] || {};
    // Use Monday's value for weekend unless rows differ — simplest behavior per user intent.
    const v = row.mon || row.tue || row.wed || row.thu || row.fri || "";
    const next = { ...(grid[rowKey] || {}) };
    WEEKEND.forEach((d) => { next[d] = v; });
    onChange({ ...grid, [rowKey]: next });
    toast.success("Weekday value copied into Sat/Sun");
  };
  const clearRow = (rowKey) => {
    const next = { ...(grid[rowKey] || {}) };
    DAYS.forEach((d) => { next[d] = ""; });
    onChange({ ...grid, [rowKey]: next });
  };
  return (
    <div className="overflow-x-auto rounded-xl border border-[#D6E3EF]">
      <table className="data-table min-w-[900px]">
        <thead>
          <tr>
            <th style={{ width: 220 }}>Level of Care / Activity</th>
            {DAY_LABELS.map((d) => <th key={d} className="text-center" style={{ width: 70 }}>{d}</th>)}
            <th style={{ width: 100 }} className="text-center">Quick</th>
          </tr>
        </thead>
        <tbody>
          {rowTypes.map((r) => {
            const row = grid[r.key] || {};
            return (
              <tr key={r.key} data-testid={`${testidPrefix}-${r.key}`}>
                <td>
                  <div className="font-semibold text-[13px] text-[#0B1E3A]">{r.label}</div>
                  <input className="tb-input mt-1" style={{ height: 32, fontSize: 12 }}
                    placeholder="Specify…" value={row.specify ?? ""}
                    onChange={(e) => setSpecify(r.key, e.target.value)}
                    data-testid={`${testidPrefix}-${r.key}-specify`} />
                </td>
                {DAYS.map((d) => (
                  <td key={d}>
                    <input className="tb-input" style={{ height: 36, fontSize: 12, padding: "0 8px" }}
                      placeholder="time"
                      value={row[d] ?? ""}
                      onChange={(e) => setCell(r.key, d, e.target.value)}
                      data-testid={`${testidPrefix}-${r.key}-${d}`} />
                  </td>
                ))}
                <td className="text-center">
                  <div className="inline-flex flex-col items-stretch gap-1">
                    <button type="button"
                      onClick={() => copyMondayAll(r.key)}
                      title="Copy Monday's value to all 7 days"
                      className="inline-flex items-center justify-center gap-1 h-6 px-2 rounded-md bg-[#E7F1FB] border border-[#B7CADA] text-[10.5px] font-semibold text-[#0A3D79] hover:bg-[#d9e6df]"
                      data-testid={`${testidPrefix}-${r.key}-copy-all`}>
                      <CalendarDays className="w-3 h-3" /> All 7
                    </button>
                    <button type="button"
                      onClick={() => copyWeekdaysToWeekend(r.key)}
                      title="Copy weekday value into weekend"
                      className="inline-flex items-center justify-center gap-1 h-6 px-2 rounded-md bg-white border border-[#B7CADA] text-[10.5px] font-semibold text-[#0A3D79] hover:bg-[#E7F1FB]"
                      data-testid={`${testidPrefix}-${r.key}-copy-weekend`}>
                      <Copy className="w-3 h-3" /> Wknd
                    </button>
                    <button type="button"
                      onClick={() => clearRow(r.key)}
                      title="Clear this row's day cells"
                      className="inline-flex items-center justify-center gap-1 h-6 px-2 rounded-md bg-white border border-[#D6E3EF] text-[10.5px] text-[#3A5275] hover:bg-[#E7F1FB]"
                      data-testid={`${testidPrefix}-${r.key}-clear`}>
                      Clear
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function ScheduleGrid({ rows, onChange, testidPrefix, taskLabel }) {
  // Assign each row a stable client-side id on first render so React
  // reconciliation survives deletions. Without this, removing row N would
  // cause React to reuse row N+1's DOM node for row N's new data, briefly
  // flashing the wrong text into the wrong cell. Matches the RowEditor
  // pattern in Step5Medical.
  const idMap = useRef(new WeakMap());
  const getRowId = (row) => {
    let id = idMap.current.get(row);
    if (!id) {
      id = `${testidPrefix}-${Math.random().toString(36).slice(2, 10)}`;
      idMap.current.set(row, id);
    }
    return id;
  };
  const rowsWithIds = useMemo(
    () => (rows || []).map((r) => ({ row: r, id: getRowId(r) })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rows]
  );

  // New rows default to all 7 days selected (per RN request: "default times per week is 7")
  const setCell = (idx, key, value) => onChange(rows.map((r, i) => i === idx ? { ...r, [key]: value } : r));
  const toggleDay = (idx, day) => setCell(idx, day, !rows[idx][day]);
  const add = () => {
    const allDays = {};
    DAYS.forEach((d) => { allDays[d] = true; });
    onChange([...(rows || []), { time: "", task: "", ...allDays }]);
  };
  const setAllDaysForRow = (idx, value) => {
    const next = rows.map((r, i) => {
      if (i !== idx) return r;
      const patch = { ...r };
      DAYS.forEach((d) => { patch[d] = value; });
      return patch;
    });
    onChange(next);
  };
  const copyWeekdayToWeekend = (idx) => {
    const row = rows[idx];
    if (!row) return;
    const on = WEEKDAYS.some((d) => row[d]);
    onChange(rows.map((r, i) => i === idx ? { ...r, sat: on, sun: on } : r));
    toast.success("Weekday → Sat/Sun");
  };
  const remove = (idx) => onChange(rows.filter((_, i) => i !== idx));

  return (
    <div>
      <div className="overflow-x-auto rounded-xl border border-[#D6E3EF]">
        <table className="data-table min-w-[860px]">
          <thead>
            <tr>
              <th style={{ width: 110 }}>Time</th>
              <th style={{ minWidth: 220 }}>{taskLabel}</th>
              {DAY_LABELS.map((d) => <th key={d} className="text-center" style={{ width: 44 }}>{d}</th>)}
              <th style={{ width: 120 }} className="text-center">Quick</th>
              <th style={{ width: 40 }}></th>
            </tr>
          </thead>
          <tbody>
            {rowsWithIds.map(({ row: r, id }, idx) => (
              <tr key={id} data-testid={`${testidPrefix}-row-${idx}`}>
                <td>
                  <input className="tb-input" style={{ height: 40 }} placeholder="09:00"
                    value={r.time ?? ""} onChange={(e) => setCell(idx, "time", e.target.value)}
                    data-testid={`${testidPrefix}-${idx}-time`} />
                </td>
                <td>
                  <input className="tb-input" style={{ height: 40 }} placeholder={taskLabel}
                    value={r.task ?? ""} onChange={(e) => setCell(idx, "task", e.target.value)}
                    data-testid={`${testidPrefix}-${idx}-task`} />
                  {r.catalog_key && (
                    <div
                      className="mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-[#FFFDEC] border border-[#DBB90A] text-[9.5px] uppercase tracking-[0.12em] font-bold text-[#8A6B00]"
                      data-testid={`${testidPrefix}-${idx}-standard-badge`}
                      title="Pre-populated from the standard PCW service catalog. Untick days or remove the row if not needed for this client."
                    >
                      Standard
                    </div>
                  )}
                </td>
                {DAYS.map((d) => (
                  <td key={d} className="text-center">
                    <button type="button" onClick={() => toggleDay(idx, d)}
                      className={`w-8 h-8 rounded-md border text-[11px] font-semibold transition ${r[d] ? "bg-[#0A3D79] border-[#0A3D79] text-[#F7FAFC]" : "bg-white border-[#D6E3EF] text-[#1FA0E6] hover:border-[#1FA0E6]"}`}
                      data-testid={`${testidPrefix}-${idx}-${d}`}>
                      {r[d] ? "✓" : "·"}
                    </button>
                  </td>
                ))}
                <td className="text-center">
                  <div className="inline-flex flex-col gap-1">
                    <button type="button" onClick={() => setAllDaysForRow(idx, true)}
                      className="inline-flex items-center justify-center gap-1 h-6 px-2 rounded-md bg-[#E7F1FB] border border-[#B7CADA] text-[10.5px] font-semibold text-[#0A3D79] hover:bg-[#d9e6df]"
                      data-testid={`${testidPrefix}-${idx}-all7`}>
                      All 7
                    </button>
                    <button type="button" onClick={() => copyWeekdayToWeekend(idx)}
                      className="inline-flex items-center justify-center gap-1 h-6 px-2 rounded-md bg-white border border-[#B7CADA] text-[10.5px] font-semibold text-[#0A3D79] hover:bg-[#E7F1FB]"
                      data-testid={`${testidPrefix}-${idx}-copywknd`}>
                      Wknd
                    </button>
                    <button type="button" onClick={() => setAllDaysForRow(idx, false)}
                      className="inline-flex items-center justify-center gap-1 h-6 px-2 rounded-md bg-white border border-[#D6E3EF] text-[10.5px] text-[#3A5275] hover:bg-[#E7F1FB]"
                      data-testid={`${testidPrefix}-${idx}-none`}>
                      None
                    </button>
                  </div>
                </td>
                <td>
                  <button type="button" onClick={() => remove(idx)} className="text-[#c05746] p-2 hover:bg-[#fbeae5] rounded-md" data-testid={`${testidPrefix}-remove-${idx}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
            {(!rows || rows.length === 0) && (
              <tr><td colSpan={11} className="text-center text-[#3A5275] py-6">No rows yet — tap “Add row” to begin. New rows default to all 7 days selected.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <button type="button" onClick={add} className="btn-ghost mt-3" data-testid={`${testidPrefix}-add`}>
        <Plus className="w-4 h-4" /> Add row (defaults to 7 days)
      </button>
    </div>
  );
}

export default function Step6Social() {
  const { data, update, setSection } = useWizard();

  // Auto-populate the PCW staffing schedule with the standard service catalog
  // the first time the nurse lands on Step 6 with an empty grid. The RN then
  // unticks days or removes rows that don't apply to this client — far faster
  // than typing every service from scratch in the home.
  useEffect(() => {
    if (!data.staffing_schedule || data.staffing_schedule.length === 0) {
      setSection("staffing_schedule", buildDefaultStaffingSchedule());
    }
    // We only ever auto-populate once on first arrival to Step 6 with an
    // empty grid; subsequent edits (including a deliberate clear-all) are
    // respected. The RN can re-load the catalog via the explicit button.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resetStaffingToCatalog = () => {
    setSection("staffing_schedule", buildDefaultStaffingSchedule());
    toast.success("Reloaded the standard PCW service catalog (12 rows, all 7 days).");
  };

  return (
    <div className="space-y-10">
      <section className="card-surface fade-up">
        <SectionHeading eyebrow="Step 6 · Section L" title="Social & Environmental Factors"
          description="Home, support, transportation, and financial context." />
        <Grid cols={2}>
          <TextInput label="Lives With" testid="social-lives"
            value={data.social.lives_with} onChange={(v) => update("social", { lives_with: v })} />
          <TextInput label="Financial Concerns (quick summary)" testid="social-financial"
            value={data.social.financial} onChange={(v) => update("social", { financial: v })} />
        </Grid>
        <div className="mt-3 text-[12px] text-[#3A5275] space-y-1" data-testid="social-moved-notes">
          <div>
            Caregiver support / burden now lives on <Link to="/wizard/7" className="text-[#0A3D79] underline">Step 7 · Caregiver Capability + Burden</Link> — 5 structured go/no-go flags + Zarit-lite score.
          </div>
          <div>
            Transportation now lives on <Link to="/wizard/7" className="text-[#0A3D79] underline">Step 7 · Transportation</Link> — drives self / means of transport / appointment assistance.
          </div>
          <div>
            Detailed financial status now lives on <Link to="/wizard/7" className="text-[#0A3D79] underline">Step 7 · Financial</Link> — handling, inappropriate income use, uncovered expenses.
          </div>
        </div>
        <div className="mt-5 space-y-4">
          <NarrativeField label="Home Environment / Safety" testid="social-home"
            fieldKey="social.home_environment"
            fieldPurpose="Physical environment, safety hazards, stairs, clutter, heat/cool, emergency egress. Anything that affects safe PC delivery."
            value={data.social.home_environment} onChange={(v) => update("social", { home_environment: v })} />
          <NarrativeField label="Social Engagement" testid="social-engagement"
            fieldKey="social.engagement"
            fieldPurpose="Social participation, isolation risk, family contact, community activities."
            value={data.social.engagement} onChange={(v) => update("social", { engagement: v })} />
        </div>
      </section>

      <section className="card-surface fade-up">
        <SectionHeading eyebrow="Step 6 · F-11136 Element 13" title="Activity Schedule (by activity type)"
          description="Official F-11136 keyed rows. Use the “All 7” button to fill once across every day of the week." />
        <KeyedGrid rowTypes={ACTIVITY_ROW_TYPES}
          grid={data.f11136_activity_grid || {}}
          onChange={(v) => setSection("f11136_activity_grid", v)}
          testidPrefix="f11136-activity" />
      </section>

      <section className="card-surface fade-up">
        <SectionHeading eyebrow="Step 6 · F-11136 Element 15" title="Staffing Schedule (by provider type)"
          description="Enter the time(s) of day each provider delivers services. Same quick-apply buttons." />
        <KeyedGrid rowTypes={STAFFING_ROW_TYPES}
          grid={data.f11136_staffing_grid || {}}
          onChange={(v) => setSection("f11136_staffing_grid", v)}
          testidPrefix="f11136-staffing" />
        <div className="mt-4">
          <NarrativeField label="16. Other Information" testid="f11136-other-info"
            fieldKey="f11136_other_info"
            fieldPurpose="F-11136 Element 16 — free-form supporting context for weekly hours request."
            value={data.f11136_other_info}
            onChange={(v) => setSection("f11136_other_info", v)} />
        </div>
      </section>

      <section className="card-surface fade-up">
        <SectionHeading eyebrow="Step 6 · Day-by-Day Activity Schedule" title="Client Activity Schedule"
          description="New rows default to all 7 days checked; tap Wknd to copy weekday selections to Sat/Sun." />
        <ScheduleGrid
          rows={data.activity_schedule}
          onChange={(v) => setSection("activity_schedule", v)}
          testidPrefix="activity"
          taskLabel="Activity / event" />
      </section>

      <section className="card-surface fade-up">
        <SectionHeading eyebrow="Step 6 · Day-by-Day Staffing Schedule" title="PCW Staffing Schedule"
          description="Pre-populated with the standard PCW service catalog (12 rows, all 7 days checked). Untick days that don't apply, edit the task wording, or remove rows entirely. The Reset button reloads the full catalog if you cleared it." />
        <div className="mb-3 flex flex-wrap items-center gap-3 text-[12.5px]">
          <div
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#E7F1FB] border border-[#C1D7EF] text-[#0A3D79] font-semibold"
            data-testid="staffing-prepopulated-note"
          >
            <ListChecks className="w-3.5 h-3.5" />
            Standard catalog loaded — untick what doesn&apos;t apply.
          </div>
          <button
            type="button"
            onClick={resetStaffingToCatalog}
            className="btn-ghost !h-8 !px-3 !text-[12px]"
            data-testid="staffing-reset-catalog"
            title="Replaces the current PCW grid with the full default service catalog"
          >
            <RotateCcw className="w-3.5 h-3.5" /> Reset to default catalog
          </button>
        </div>
        <ScheduleGrid
          rows={data.staffing_schedule}
          onChange={(v) => setSection("staffing_schedule", v)}
          testidPrefix="staffing"
          taskLabel="PCW task" />
      </section>
    </div>
  );
}
